/**
 * The JS half of the Rust→JS→Rust round trip.
 *
 * Rust emits `mcp:request` `{ reqId, tool, args }` from the socket thread and
 * blocks on an mpsc receiver; this answers with `mcp_respond`. The gate, the
 * rate limit and the argument-schema check have all already run in Rust before
 * the emit — nothing here is a security boundary, and nothing here should
 * start behaving like one.
 *
 * `@tauri-apps/api/*` is imported LAZILY here, inside the function, per the
 * convention `tauriImportsAreLazy.test.js` pins. Note what that does and does
 * not buy: this module's own graph reaches `@tauri-apps/api/core` statically
 * anyway, through `handlers/documents.js` and `documentsMemory.js` — the same
 * shape `documentsStore`/`boardsStore` already have, which jest maps. So the
 * lazy import is the house style, not a property of the graph.
 */
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { runHandler } from './handlers';
import { mcpError } from './errors';
import { signInRequired, SIGN_IN_REQUIRED_MESSAGE } from '../plans/claimExpiryState';

export const MCP_REQUEST_EVENT = 'mcp:request';

async function respond(reqId, outcome) {
  const { invoke } = await import('@tauri-apps/api/core');
  await invoke('mcp_respond', {
    reqId,
    ok: outcome.ok,
    result: outcome.ok ? outcome.result : null,
    error: outcome.ok ? null : outcome.error,
  });
}

/**
 * Subscribes to `mcp:request` and dispatches. Returns the subscription so a
 * React effect can `.unlisten()` it.
 *
 * `getContext` is called per request rather than captured, so a language
 * change (which renames the Agent Notes folder) is picked up without
 * re-subscribing.
 *
 * `onAnswered(tool, ok)` is told after each answer — the usage counter hangs
 * off it in `hooks/useMcpBus.js`, so nothing telemetry enters this graph.
 */
export function startMcpBus(getContext, onAnswered = null) {
  return subscribeTauriEvent(MCP_REQUEST_EVENT, async (event) => {
    const payload = event?.payload || {};
    const { reqId, tool, args, caller } = payload;
    if (reqId == null) return;
    // `caller` ({ agent }) is Rust's reading of the MCP client's identity
    // (client_agent.rs) — beside the arguments, never inside them.
    // Plans v2 (owner, 16.10.2026): offline past the plan claim's 14 days the
    // app requires sign-in, and nothing is served until it is confirmed again.
    const outcome = signInRequired()
      ? { ok: false, error: mcpError('unavailable', SIGN_IN_REQUIRED_MESSAGE) }
      : await runHandler(tool, args, { ...(getContext ? getContext() : {}), caller: caller || null });
    try {
      if (onAnswered) onAnswered(tool, outcome.ok === true);
    } catch {
      // a counter must never stop the answer
    }
    try {
      await respond(reqId, outcome);
    } catch {
      // The socket thread has its own timeout; a failed respond is a slow
      // tool from the client's point of view, not a hang.
    }
  });
}
